/* eslint-disable max-lines */
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  SimpleChanges,
  OnChanges,
  Inject,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { noop, takeUntil } from 'rxjs';
import { SweetAlertOptions } from 'sweetalert2';

import {
  Company,
  DashpivotModules,
  EventTypes,
  ILegacyList,
  PlanTypeLimits,
  Project,
} from '@site-mate/dashpivot-shared-library';
import { CompanyPlanTypes } from '@site-mate/sitemate-global-shared/lib';

import { WarningModalComponent } from 'app/shared/component/warning-modal/warning-modal.component';
import { WINDOW } from 'app/shared/factory/window.factory';
import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { ExportResponse } from 'app/shared/model/export-response.model';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { DataCompressionService } from 'app/shared/service/data-compression/data-compression.service';
import { DownloaderService } from 'app/shared/service/downloader.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { EventsService } from 'app/shared/service/events/events.service';
import { LocalStorageService } from 'app/shared/service/local-storage.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UserService } from 'app/user/user.service';
import { environment } from 'environments/environment';

import { BulkApplyListRowStyleModal } from '../bulk-apply-list-row-style/bulk-apply-list-row-style-modal.component';
import { ListImportModalComponent } from '../list-import-modal/list-import-modal.component';
import { ListImportProgressModalComponent } from '../list-import-progress/list-import-progress-modal.component';
import { ListItemSelectionService } from '../list-item-selection.service';
import { ListsService } from '../lists.service';

@Component({
  selector: 'cc-list-detail-tool-bar',
  templateUrl: './list-detail-tool-bar.component.html',
  styleUrls: ['./list-detail-tool-bar.component.scss', '../../../styles/components/tool-bar.component.scss'],
})
export class ListDetailToolBarComponent implements OnChanges {
  @Input() list: ILegacyList;
  @Input() loading = false;
  @Input() updating = false;
  @Input() editExisting = false;
  @Input() listIsDeployed = false;
  @Input() canAddNewItem = false;
  @Input() folder: {
    hierarchy: Exclude<AppHierarchy, 'team'>;
    project?: Project;
    company?: Company;
    planType?: CompanyPlanTypes;
  };
  @Input() triggerUploadListCSV: EventEmitter<void>;
  @Output() readonly listNameUpdated = new EventEmitter<string>();
  @Output() readonly addListItemStarted = new EventEmitter<void>();
  @Output() readonly listUploaded = new EventEmitter();
  @Output() readonly listDeployedViaCsv = new EventEmitter<{ fileList: FileList; list: ILegacyList }>();
  @Output() readonly deleteItems = new EventEmitter<string[]>();
  @Output() readonly listItemStylesUpdated = new EventEmitter<{
    fullList: ILegacyList;
  }>();
  isEscPressed = false;
  hasPermissionToEdit = false;

  private unsubscribe = new EventEmitter<void>();
  selectedItemsSize = 0;
  newListImport = environment.featureToggles.newListImport;
  readonly planTypeLimits = PlanTypeLimits;
  readonly dashpivotModules = DashpivotModules;
  private readonly modalOptions = { centered: true, windowClass: 'sm-flex sm-items-center' };

  @ViewChild('fileUploadInput') fileUploadInput: ElementRef<HTMLInputElement>;
  @ViewChild('listNameInput') listNameInput: ElementRef<HTMLInputElement>;

  constructor(
    private readonly modal: NgbModal,
    private readonly listItemSelectionService: ListItemSelectionService,
    private readonly confirmService: ConfirmService,
    private readonly eventsService: EventsService,
    private readonly listsService: ListsService,
    private readonly toastrService: ToastrService,
    private readonly errorHandler: ErrorHandler,
    protected readonly downloaderService: DownloaderService,
    private readonly localStorageService: LocalStorageService,
    private readonly dataCompressionService: DataCompressionService,
    @Inject(WINDOW) private readonly window: Window,
    private readonly userService: UserService,
  ) {}

  ngOnInit() {
    this.triggerUploadListCSV?.pipe(takeUntil(this.unsubscribe)).subscribe(async () => {
      await this.uploadList();
    });

    this.listItemSelectionService.selectedItems$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((selectedItems) => {
        this.selectedItemsSize = selectedItems.size;
      });

    this.hasPermissionToEdit =
      this.folder?.hierarchy === AppHierarchy.Project
        ? this.userService.isProjectControllerOrAbove()
        : this.userService.isCompanyControllerOrAbove();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.list && this.listNameInput) {
      this.updateInputSize(this.listNameInput.nativeElement, changes.list.currentValue.name);
    }
  }

  downloadList() {
    this.exportCSV();
  }

  uploadList() {
    return this.openListOverwriteModal()
      .then(() => this.openFileUpload())
      .catch(noop);
  }

  openNewImportModal(): Promise<void> {
    return this.openListPropertyWarningModal()
      .then(() => this.openListOverwriteModal())
      .then(() => this.openImportModal())
      .catch(noop);
  }

  private openListOverwriteModal(): Promise<void> {
    if (!this.list.items.length) {
      return Promise.resolve();
    }

    const modalRef = this.modal.open(WarningModalComponent, this.modalOptions);
    const messageKey = this.listIsDeployed ? 'deployedListOverwrite' : 'nonDeployedListOverwrite';
    modalRef.componentInstance.titleKey = 'warning';
    modalRef.componentInstance.messageKey = messageKey;
    modalRef.componentInstance.continueLabel = 'Overwrite';

    return modalRef.result;
  }

  private openFileUpload() {
    this.fileUploadInput.nativeElement.click();
  }

  openListPropertyWarningModal(): Promise<void> {
    if (this.list.properties.length > 0) {
      return Promise.resolve();
    }
    const modalRef = this.modal.open(WarningModalComponent, this.modalOptions);
    modalRef.componentInstance.titleKey = 'listPropertyRequiredTitle';
    modalRef.componentInstance.messageKey = 'listPropertyRequiredBody';
    modalRef.componentInstance.continueLabel = 'Continue Upload';

    return modalRef.result;
  }

  openImportModal() {
    const modalRef = this.modal.open(ListImportModalComponent, this.modalOptions);

    modalRef.componentInstance.list = this.list;
    modalRef.componentInstance.folder = this.folder;
    modalRef.result.then(this.openImportProgressModal.bind(this)).catch(noop);
  }

  private openImportProgressModal(result: ExportResponse) {
    if (result?.id) {
      const modalRef = this.modal.open(ListImportProgressModalComponent, {
        ...this.modalOptions,
        backdrop: 'static',
        keyboard: false,
      });
      modalRef.componentInstance.sessionParameters = result;
    }
  }

  onFileUpload(fileList: FileList) {
    if (this.listIsDeployed) {
      this.listDeployedViaCsv.emit({ list: this.list, fileList });
    } else {
      this.listUploaded.emit({ list: this.list, fileList });
    }

    this.eventsService.trackEvent(EventTypes.ListItemActionApplied, { Context: 'Uploaded From CSV' });
  }

  addListItem() {
    this.addListItemStarted.emit();
  }

  updateListName(name: string) {
    if (this.isEscPressed || name?.trim() === this.list?.name?.trim()) {
      this.isEscPressed = false;
      return;
    }

    this.listNameInput.nativeElement.blur();
    this.listsService.updateList(this.folder, this.list.id, { name: name.trim() }).subscribe({
      next: () => {
        this.toastrService.successByKey('listNameUpdated');
        this.list.name = name;
      },
      error: (error) => {
        this.errorHandler.handle(error);
        this.listNameInput.nativeElement.value = this.list.name;
      },
    });

    if (this.listNameInput) {
      this.listNameInput.nativeElement.scrollLeft = 0;
    }
  }

  updateInputSize(inputElement: HTMLInputElement, textValue?: string) {
    inputElement.parentElement.dataset.value = textValue || inputElement.value;
  }

  onEscapePress() {
    this.listNameInput.nativeElement.value = this.list?.name || '';
    this.isEscPressed = true;
    this.listNameInput.nativeElement.blur();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  onDeleteItems(): void {
    const options: SweetAlertOptions = { confirmButtonText: 'Delete', confirmButtonColor: '#dc3545' };

    if (this.selectedItemsSize > 1) {
      options.html = `Are you sure you want to delete the ${this.selectedItemsSize} selected items?`;
    }

    this.confirmService
      .confirm('deleteListItem', options)
      .then(() => {
        const items = Array.from(this.listItemSelectionService.selectedItems);
        this.listItemSelectionService.clearSelection();
        this.deleteItems.emit(items);
      })
      .catch(noop);
  }

  onExportItems(): void {
    const itemIds: string[] = Array.from(this.listItemSelectionService.selectedItems);
    this.exportCSV(itemIds);
  }

  onStartBulkApplyItemColour() {
    const modalRef = this.modal.open(BulkApplyListRowStyleModal, { windowClass: 'sm-flex sm-items-center' });
    modalRef.componentInstance.parent = {
      hierarchy: this.folder.hierarchy,
      id: this.folder.project?._id || this.folder.company?._id,
    };
    modalRef.componentInstance.listId = this.list._id;

    modalRef.result
      .then(({ fullList }) => {
        this.listItemStylesUpdated.emit({ fullList });
        this.listItemSelectionService.clearSelection();
      })
      .catch(noop);
  }

  private setCompressedIdsInLocalStorageItem(key: string, itemIds: string[]) {
    this.localStorageService.setItem(
      key,
      JSON.stringify({
        ids: this.dataCompressionService.compress(JSON.stringify(itemIds)),
        timestamp: Date.now(),
        type: this.downloaderService.downloaderType,
      }),
    );
  }

  exportCSV(itemIds?: string[]) {
    const queryParams: { [x: string]: string } = {
      parentId: this.folder.project?._id || this.folder.company?._id,
      hierarchy: this.folder.hierarchy.toLowerCase(),
    };

    if (itemIds) {
      const storageKey = this.downloaderService.getRandomDownloadId();
      this.setCompressedIdsInLocalStorageItem(storageKey, itemIds);
      queryParams.downloadId = storageKey;
    }

    const url = this.downloaderService.getExportListCSVUrl({ listId: this.list._id, queryParams });
    this.window.open(`${url}`, '_blank');
  }
}
